// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { clientLogger } from '@seedplanner/logger';
import { getIsReactHydrationError, labels, utils } from '@seedplanner/shared-utils';
import { routes } from '@seedplanner/shared-web';
import TraceKit from 'tracekit';
import './init';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { render } from './render';
import { sendErrorReport } from './sendErrorReport';

const currentLoadTime = Date.now();
const INITIAL_LOAD_TIME_KEY = 'initialLoadTime';

const start = () => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const currentWindow = window as any;

  const sentryDsn = currentWindow.__SENTRY_DSN__;
  if (sentryDsn != null && sentryDsn !== '') {
    clientLogger.info('Initializing Sentry');
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracePropagationTargets: ['localhost', /^https:\/\/microgreenmanager\.com/],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 0.1,
      profilesSampleRate: 1.0,
    });
  }

  TraceKit.report.subscribe((stack, _isWindowError, err) => {
    const asError = utils.asErrorInstance(err);
    const firstStack = stack.stack[0] as TraceKit.StackFrame | undefined;
    const logInfo = {
      title: 'client_uncaught_exception',
      error: asError?.message ?? 'no message',
      [labels.STACK_MESSAGE]: stack.message,
      [labels.STACK_LINENUMBER]: firstStack === undefined ? undefined : firstStack.line,
      [labels.STACK_COLUMNNUMBER]: firstStack === undefined ? undefined : firstStack.column,
    };
    clientLogger.error({ ...logInfo });
    const message = stack?.message ?? 'no message';
    const isReactHydrationError = getIsReactHydrationError(message);
    sendErrorReport({
      body: {
        message,
        errorName: asError?.name ?? 'unknown',
        location: window.location.pathname,
        lineNumber: firstStack === undefined ? undefined : firstStack.line,
        columnNumber: firstStack === undefined ? undefined : firstStack.column,
        functionName: firstStack === undefined ? undefined : firstStack.func,
        url: firstStack === undefined ? undefined : firstStack.url,
        fullStack: stack?.stack?.map(({ url, func, line, column }) => `${url}:${line}:${column} - ${func}`).join('\n'),
        nonce: currentWindow.__NONCE__ ?? undefined,
        clientHtml: isReactHydrationError ? document.body.innerHTML : undefined,
        currentLoadTime,
        initialLoadTime: sessionStorage.getItem(INITIAL_LOAD_TIME_KEY) ?? undefined,
      },
      labels: logInfo,
      endpoint: routes.TRACEKIT,
    })
      .then(() => {
        clientLogger.info({ ...logInfo }, 'Error report sent');
      })
      .catch((reportError) => {
        clientLogger.error({ ...logInfo, error: reportError }, 'Error report failed to send');
      });
  });
  TraceKit.collectWindowErrors = true;

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent | undefined) => {
    let error = new Error('Unhandled rejection- MM');
    if (event && event.reason instanceof Error) {
      error = event.reason;
    }

    const logInfo = { title: 'client_unhandled_rejection', error: error.message };
    clientLogger.error({ ...logInfo }, 'Window unhandled rejection');
    if (event) {
      event.preventDefault();
    }
  });

  const maybeExistingInitialLoadTime = sessionStorage.getItem(INITIAL_LOAD_TIME_KEY);
  if (maybeExistingInitialLoadTime == null) {
    sessionStorage.setItem(INITIAL_LOAD_TIME_KEY, Date.now().toString());
  }

  render();
};

start();
